'use client';

/**
 * CalendarToolbar — shared toolbar chrome for CalendarBox and
 * LiveCalendarBox. Both blocks render the same nav (prev/next/today) +
 * centered title + Work-week/Work-hour toggle-chip layout; this keeps
 * them pixel-identical instead of two hand-drifted implementations.
 * CalendarBox additionally passes `trailing` (view tabs + Add-event
 * button) which LiveCalendarBox omits.
 */
import type { CSSProperties, ReactNode } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { Button } from '@akademi/ui';

const TOKEN = {
  brand: 'var(--color-brand-500, #29a9e0)',
  brand100: 'var(--color-brand-100, #c9ecf8)',
  brand700: 'var(--color-brand-700, #166a98)',
  border: 'var(--color-border-subtle, rgba(0,0,0,0.08))',
  text: 'var(--color-text-primary, #18181b)',
  textSec: 'var(--color-text-secondary, #52525b)',
  radius: 'var(--radius-md, 10px)',
  onBrand: 'var(--color-text-on-brand, #fff)',
};

/**
 * Self-contained responsive rules for the toolbar — injected here (not in
 * the host block) so both CalendarBox and LiveCalendarBox get identical
 * mobile behavior regardless of which one renders first. Uses `!important`
 * on the title's flex-basis because the title carries an inline
 * `flex: 1` (needed for desktop centering) which a plain class rule can
 * never win against in the cascade.
 */
const CalendarToolbarStyles = (): null => {
  if (typeof document === 'undefined') return null;
  const id = 'ak-calendar-toolbar-styles';
  if (!document.getElementById(id)) {
    const s = document.createElement('style');
    s.id = id;
    s.textContent = `
      @media (max-width: 767px) {
        .ak-cal-toolbar { justify-content: center !important; }
        .ak-cal-toolbar > span[data-title] {
          order: -1 !important;
          flex: 1 1 100% !important;
          text-align: center !important;
          margin-bottom: 6px !important;
          font-size: 16px !important;
        }
        .ak-cal-toolbar-group { flex: 1 1 auto !important; justify-content: center !important; }
      }
    `;
    document.head.appendChild(s);
  }
  return null;
};

export function ToggleChip({
  active,
  label,
  onToggle,
}: {
  active: boolean;
  label: string;
  onToggle: () => void;
}) {
  const style: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '5px 10px',
    borderRadius: TOKEN.radius,
    border: `1px solid ${active ? TOKEN.brand : TOKEN.border}`,
    background: active ? TOKEN.brand100 : 'transparent',
    cursor: 'pointer',
    fontSize: 'var(--font-size-xs, 12px)',
    fontWeight: 600,
    color: active ? TOKEN.brand700 : TOKEN.textSec,
    whiteSpace: 'nowrap',
  };
  return (
    <button
      type="button"
      className="ak-focus"
      style={style}
      onClick={onToggle}
      aria-pressed={active}
    >
      <span
        style={{
          display: 'inline-block',
          width: 14,
          height: 14,
          borderRadius: 'var(--radius-xs, 3px)',
          background: active ? TOKEN.brand : TOKEN.border,
          flexShrink: 0,
        }}
      />
      {label}
    </button>
  );
}

export interface CalendarToolbarProps {
  title: string;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  todayLabel?: string;
  prevLabel?: string;
  nextLabel?: string;
  workWeek: boolean;
  workHour: boolean;
  onToggleWorkWeek: () => void;
  onToggleWorkHour: () => void;
  workWeekLabel: string;
  workHourLabel: string;
  /** Extra trailing controls — e.g. CalendarBox's view tabs + Add-event button. */
  trailing?: ReactNode;
}

export function CalendarToolbar({
  title,
  onPrev,
  onNext,
  onToday,
  todayLabel = 'Today',
  prevLabel = 'Previous',
  nextLabel = 'Next',
  workWeek,
  workHour,
  onToggleWorkWeek,
  onToggleWorkHour,
  workWeekLabel,
  workHourLabel,
  trailing,
}: CalendarToolbarProps) {
  return (
    <div
      className="ak-cal-toolbar"
      style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}
    >
      <CalendarToolbarStyles />
      <div
        className="ak-cal-toolbar-group"
        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
      >
        <Button variant="brand-outlined" size="icon" onClick={onPrev} aria-label={prevLabel}>
          <ChevronLeftIcon size={16} />
        </Button>
        <Button variant="default" size="sm" onClick={onToday}>
          {todayLabel}
        </Button>
        <Button variant="brand-outlined" size="icon" onClick={onNext} aria-label={nextLabel}>
          <ChevronRightIcon size={16} />
        </Button>
      </div>

      <span
        data-title
        style={{
          flex: 1,
          fontSize: 'clamp(15px, 2vw, 18px)',
          fontWeight: 800,
          color: TOKEN.text,
          textAlign: 'center',
          minWidth: 0,
        }}
      >
        {title}
      </span>

      <div
        className="ak-cal-toolbar-group"
        style={{ display: 'flex', alignItems: 'center', gap: 8 }}
      >
        <ToggleChip active={workWeek} label={workWeekLabel} onToggle={onToggleWorkWeek} />
        <ToggleChip active={workHour} label={workHourLabel} onToggle={onToggleWorkHour} />
      </div>

      {trailing && (
        <div
          className="ak-cal-toolbar-group"
          style={{ display: 'flex', alignItems: 'center', gap: 10 }}
        >
          {trailing}
        </div>
      )}
    </div>
  );
}
